import {
  ArrowLeftRight,
  CalendarClock,
  Icon,
  type IconComponent,
  Network,
  UserMinus,
  UserPlus,
  UserRoundCheck,
  UserRoundSearch,
  UserX,
} from "@norican/icons";
import { Badge, type BadgeProps, Button, Checkbox, Field, Input, Textarea } from "@norican/react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { ActionLog } from "@/components/action-log";
import { HelpTerm } from "@/components/help-term";
import { Notice, Page } from "@/components/page";
import { PersonPicker } from "@/components/person-picker";
import { OutcomeBadge, StatusBadge } from "@/components/status";
import { control, subText, table, td, th } from "@/components/table";
import {
  type EmployeeChangeIn,
  type FutureHireIn,
  type Money,
  type OrgData,
  type PlannedExitIn,
  type Situation,
  useFutureHires,
  useMe,
  useSubmitWorkforce,
  useWorkforceChange,
  useWorkforceChanges,
  useWorkforceDecision,
  useWorkforceLog,
  useWorkforceVocab,
  type WorkforceRecord,
  type WorkforceVocab,
} from "@/lib/api";
import { BROWSER_TZ, dateInZone, errorMessage, inZone, inZoneSentence } from "@/lib/format";

/**
 * Workforce changes — Carlyne Peitgen's "Norican Org Workforce Change Input
 * Form" (2 Oct 2026) as a page, with the approval Laura Webster asked for on
 * 6 Oct and the three things Timo decided the same day:
 *
 * * **A role of its own.** Only `HROps.Workforce` holders see this page;
 *   Carlyne and Laura for now. Within it, the configured approvers get the
 *   approve / send-back controls.
 * * **Compensation on the record, restricted.** It is shown to the submitter,
 *   the responsible HRBP and the approvers, and nobody else; the screen says
 *   when it is being withheld rather than showing an empty block.
 * * **Approval does the work.** The approver's click raises the offboarding
 *   for an exit, queues the AD write for an organisational change, and hands
 *   a new hire to the helpdesk. What happened is written to the record and
 *   shown back here, with links into the queue and the Org page.
 *
 * Two views: "Report a change" mirrors the workbook's START HERE tab — pick
 * the situation, get the one form that applies; "Requests" is every record,
 * with the ones awaiting approval first for an approver.
 */

type View = "report" | "requests" | "log";
const isView = (v: string | null): v is View => v === "report" || v === "requests" || v === "log";

const ZONES = Array.from(
  new Set([
    BROWSER_TZ,
    "Europe/Copenhagen",
    "Europe/Berlin",
    "Europe/Paris",
    "Europe/Madrid",
    "Europe/Prague",
    "Europe/London",
    "Europe/Zurich",
    "Asia/Kolkata",
    "Asia/Shanghai",
    "Asia/Tokyo",
    "America/Chicago",
    "America/New_York",
    "America/Mexico_City",
    "UTC",
  ]),
);

export function WorkforcePage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const me = useMe().data;
  const enabled = me?.workforce_enabled === true;
  const vocab = useWorkforceVocab(enabled);
  const list = useWorkforceChanges(enabled);
  // `?view=` mirrors the tab, so the nav's submenu and a mailed link land on
  // the right one; an opened record (`/workforce/:id`) always means Requests.
  const [params, setParams] = useSearchParams();
  const paramView = params.get("view");
  const [view, setViewState] = useState<View>(
    id ? "requests" : isView(paramView) ? paramView : "report",
  );
  const [editing, setEditing] = useState<WorkforceRecord | null>(null);
  const setView = (next: View) => {
    setViewState(next);
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set("view", next);
        return n;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (id) {
      setViewState("requests");
    } else if (isView(paramView)) {
      setViewState(paramView);
    }
  }, [id, paramView]);

  if (me && !enabled) {
    return (
      <Page title="Workforce">
        <Notice>
          This page needs the HROps.Workforce role, which your account does not hold. Ask IT if you
          should have it.
        </Notice>
      </Page>
    );
  }

  const awaiting = list.data?.awaiting ?? 0;
  const approver = list.data?.approver === true;
  const views: [View, string][] = [
    ["report", editing ? "Edit the request" : "Report a change"],
    ["requests", `Requests${awaiting ? ` (${awaiting} awaiting approval)` : ""}`],
    ["log", "Log"],
  ];

  const open = (rid: string) => {
    setEditing(null);
    navigate(`/workforce/${rid}`);
    setView("requests");
  };
  const edit = (r: WorkforceRecord) => {
    setEditing(r);
    setView("report");
  };

  return (
    <Page
      title="Workforce"
      intro={
        <>
          Report a <HelpTerm id="workforce-change">workforce change</HelpTerm>: a hire, an exit, a
          planned exit, a future position, or a change to somebody&apos;s supervisor, job, location
          or legal entity. It goes to {approver ? "you" : "the approver"} for approval, and the
          person who submitted it is told what happened.{" "}
          <Link className="underline" to="/help/workforce">
            How it works
          </Link>
        </>
      }
      actions={
        <a
          className="text-fg-muted hover:text-fg text-xs underline"
          href="/api/workforce/export.xlsx"
        >
          Download everything as Excel
        </a>
      }
    >
      <div className="border-border mt-4 flex gap-4 border-b" role="tablist">
        {views.map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={view === key}
            onClick={() => setView(key)}
            className={
              view === key
                ? "text-fg border-fg -mb-px border-b-2 pb-2 text-sm font-medium"
                : "text-fg-muted hover:text-fg pb-2 text-sm"
            }
          >
            {label}
          </button>
        ))}
      </div>

      {vocab.isLoading ? (
        <Notice>Loading…</Notice>
      ) : vocab.error || !vocab.data ? (
        <Notice tone="danger">{errorMessage(vocab.error)}</Notice>
      ) : view === "report" ? (
        <ReportView
          vocab={vocab.data}
          editing={editing}
          onDone={(rid) => {
            setEditing(null);
            open(rid);
          }}
          onCancelEdit={() => setEditing(null)}
        />
      ) : view === "log" ? (
        <LogView enabled={enabled} />
      ) : (
        <RequestsView
          vocab={vocab.data}
          items={list.data?.items ?? []}
          approver={approver}
          selected={id ?? null}
          onSelect={(rid) => navigate(rid ? `/workforce/${rid}` : "/workforce")}
          onEdit={edit}
          loading={list.isLoading}
          error={list.error}
        />
      )}
    </Page>
  );
}

// ─── Report a change ─────────────────────────────────────────────────────────

function ReportView({
  vocab,
  editing,
  onDone,
  onCancelEdit,
}: {
  vocab: WorkforceVocab;
  editing: WorkforceRecord | null;
  onDone: (id: string) => void;
  onCancelEdit: () => void;
}) {
  const [situation, setSituation] = useState<string>("");
  const picked = vocab.situations.find((s) => s.id === situation) ?? null;
  const kind = editing?.kind ?? picked?.kind ?? null;

  return (
    <section className="mt-6 space-y-6">
      {editing ? (
        <div className="border-warning-border bg-warning-bg rounded border p-3 text-sm">
          Editing the request for <strong>{editing.subject_name}</strong>
          {editing.status === "returned" && editing.decision_note ? (
            <>
              , which was sent back with the note: <em>{editing.decision_note}</em>
            </>
          ) : null}
          . Saving submits it again.{" "}
          <button className="underline" onClick={onCancelEdit}>
            Stop editing
          </button>
        </div>
      ) : (
        <SituationPicker situations={vocab.situations} value={situation} onChange={setSituation} />
      )}
      {kind === "employee_change" ? (
        <EmployeeChangeForm
          key={editing?.id ?? situation}
          vocab={vocab}
          initialType={editing?.change_type ?? picked?.change_type ?? "supervisor"}
          editing={editing}
          onDone={onDone}
        />
      ) : kind === "planned_exit" ? (
        <PlannedExitForm
          key={editing?.id ?? "new"}
          vocab={vocab}
          editing={editing}
          onDone={onDone}
        />
      ) : kind === "future_hire" ? (
        <FutureHireForm
          key={editing?.id ?? "new"}
          vocab={vocab}
          editing={editing}
          onDone={onDone}
        />
      ) : null}
    </section>
  );
}

/**
 * The START HERE tab as cards rather than a dropdown: one per situation, with
 * an icon, the sentence from the workbook, and what has to follow later. A
 * picker you can read at a glance beats one you have to open to compare.
 */
const SITUATION_ICON: Record<string, IconComponent> = {
  new_hire: UserPlus,
  exit_listed: UserMinus,
  exit_unlisted: UserX,
  exit_replacement: ArrowLeftRight,
  planned_exit: CalendarClock,
  org_change: Network,
  future_hire: UserRoundSearch,
  future_hire_filled: UserRoundCheck,
};

function SituationPicker({
  situations,
  value,
  onChange,
}: {
  situations: Situation[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <fieldset>
      <legend className="text-fg text-sm font-medium">What are you reporting?</legend>
      <p className={`${subText} mt-0.5`}>
        Pick the situation; the form that applies appears below. The same eight as the START HERE
        tab of the workbook.
      </p>
      <div role="radiogroup" aria-label="Situation" className="mt-3 grid gap-2 sm:grid-cols-2">
        {situations.map((s) => {
          const selected = s.id === value;
          return (
            <button
              key={s.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(s.id)}
              className={`flex items-start gap-3 rounded border p-3 text-left transition-colors ${
                selected
                  ? "border-fg bg-bg-subtle"
                  : "border-border hover:border-fg-muted hover:bg-bg-subtle"
              }`}
            >
              <span
                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded ${
                  selected ? "bg-fg text-bg" : "bg-bg-subtle text-fg-muted"
                }`}
              >
                <Icon icon={SITUATION_ICON[s.id] ?? Network} size="sm" />
              </span>
              <span className="min-w-0">
                <span className="text-fg block text-sm font-medium">{s.label}</span>
                <span className={`${subText} block`}>
                  {KIND_FORM[s.kind]}
                  {s.later ? ` · later: ${s.later}` : ""}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

const KIND_FORM: Record<string, string> = {
  employee_change: "Employee change form",
  planned_exit: "Planned exit form",
  future_hire: "Future hire form",
};

// ─── shared form bits ────────────────────────────────────────────────────────

function Sel({
  label,
  value,
  onChange,
  options,
  required,
  hint,
  allowEmpty = true,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[] | Array<{ value: string; label: string }>;
  required?: boolean;
  hint?: string;
  allowEmpty?: boolean;
}) {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <Field label={label} required={required} hint={hint}>
      <select className={control} value={value} onChange={(e) => onChange(e.target.value)}>
        {allowEmpty ? <option value="">—</option> : null}
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

function CityInput({
  vocab,
  country,
  value,
  onChange,
  label = "City",
  required,
}: {
  vocab: WorkforceVocab;
  country: string;
  value: string;
  onChange: (v: string) => void;
  label?: string;
  required?: boolean;
}) {
  const listId = `cities-${country || "all"}`;
  const cities = country ? (vocab.cities[country] ?? []) : Object.values(vocab.cities).flat();
  return (
    <Field
      label={label}
      required={required}
      hint={country ? undefined : "Pick the country first for suggestions."}
    >
      <Input list={listId} value={value} onChange={(e) => onChange(e.target.value)} />
      <datalist id={listId}>
        {cities.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
    </Field>
  );
}

type MoneyState = { salary: string; benefits: string; employer_cost: string; currency: string };
const EMPTY_MONEY: MoneyState = { salary: "", benefits: "", employer_cost: "", currency: "EUR" };

function moneyFrom(m: Money | null | undefined): MoneyState {
  return m
    ? {
        salary: String(m.salary),
        benefits: String(m.benefits),
        employer_cost: String(m.employer_cost),
        currency: m.currency,
      }
    : EMPTY_MONEY;
}

function moneyOut(m: MoneyState): Money | null {
  const n = (s: string) => (s.trim() === "" ? NaN : Number(s));
  const salary = n(m.salary);
  const benefits = n(m.benefits);
  const cost = n(m.employer_cost);
  if ([salary, benefits, cost].some((x) => Number.isNaN(x) || x < 0) || !m.currency) {
    return null;
  }
  return { salary, benefits, employer_cost: cost, currency: m.currency };
}

/** The three figures every compensation block asks for; all are required. */
const MONEY_FIELDS = [
  ["salary", "annual salary"],
  ["benefits", "annual benefits cost"],
  ["employer_cost", "employer cost"],
] as const;

/** The labels of the required answers still empty, in form order. */
function stillNeeded(checks: Array<[unknown, string]>): string[] {
  return checks.filter(([ok]) => !ok).map(([, label]) => label);
}

function moneyMissing(m: MoneyState, prefix: string): string[] {
  const ok = (s: string) => s.trim() !== "" && Number(s) >= 0;
  return stillNeeded([
    ...MONEY_FIELDS.map(([k, label]): [unknown, string] => [ok(m[k]), `${prefix} ${label}`]),
    [m.currency, "Currency"],
  ]);
}

function MoneyFields({
  title,
  prefix,
  value,
  onChange,
  vocab,
}: {
  title: string;
  prefix: string;
  value: MoneyState;
  onChange: (m: MoneyState) => void;
  vocab: WorkforceVocab;
}) {
  return (
    <fieldset className="border-border rounded border p-3">
      <legend className="text-fg px-1 text-sm font-medium">{title}</legend>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {MONEY_FIELDS.map(([k, label]) => (
          <Field key={k} label={`${prefix} ${label}`} required>
            <input
              type="number"
              min={0}
              step="1"
              className={control}
              value={value[k]}
              onChange={(e) => onChange({ ...value, [k]: e.target.value })}
            />
          </Field>
        ))}
        <Sel
          label="Currency"
          required
          allowEmpty={false}
          value={value.currency}
          onChange={(c) => onChange({ ...value, currency: c })}
          options={vocab.currencies}
        />
      </div>
      <p className={`${subText} mt-2`}>
        Visible to you, the responsible HRBP and the approvers only. Every read is logged.
      </p>
    </fieldset>
  );
}

/**
 * Submit is disabled until every required answer is there, and says which
 * ones are still empty, so a greyed-out button never leaves HR guessing.
 */
function SubmitRow({
  pending,
  missing,
  error,
  label,
  editing,
}: {
  pending: boolean;
  missing: string[];
  error: unknown;
  label: string;
  editing: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button type="submit" disabled={missing.length > 0 || pending}>
        {pending ? "Submitting…" : editing ? "Save and resubmit" : label}
      </Button>
      <span className={subText}>
        Fields marked <span className="text-danger-fg">*</span> are required. The approver is told
        by mail. Nothing is changed in any system until it is approved.
      </span>
      {missing.length > 0 ? (
        <p className="text-fg w-full text-sm">
          <strong>Still needed:</strong> {missing.join(", ")}.
        </p>
      ) : null}
      {error ? <p className="text-danger-fg w-full text-sm">{errorMessage(error)}</p> : null}
    </div>
  );
}

// ─── EMPLOYEE CHANGE ─────────────────────────────────────────────────────────

type OrgKey = keyof OrgData;
const ORG_KEYS: OrgKey[] = [
  "supervisor_upn",
  "job_title",
  "department",
  "area",
  "country",
  "city",
  "legal_entity",
  "brand",
];

type OrgState = Record<OrgKey, string>;
const emptyOrg = (): OrgState => Object.fromEntries(ORG_KEYS.map((k) => [k, ""])) as OrgState;
const orgFrom = (d: OrgData | undefined | null): OrgState => ({
  ...emptyOrg(),
  ...Object.fromEntries(Object.entries(d ?? {}).map(([k, v]) => [k, v ?? ""])),
});
const orgOut = (s: OrgState): OrgData =>
  Object.fromEntries(ORG_KEYS.filter((k) => s[k].trim()).map((k) => [k, s[k].trim()])) as OrgData;

function EmployeeChangeForm({
  vocab,
  initialType,
  editing,
  onDone,
}: {
  vocab: WorkforceVocab;
  initialType: NonNullable<WorkforceRecord["change_type"]>;
  editing: WorkforceRecord | null;
  onDone: (id: string) => void;
}) {
  const submit = useSubmitWorkforce();
  const futureHires = useFutureHires(true);
  const d = (editing?.details ?? {}) as Record<string, unknown>;
  const ex = (d.exit ?? {}) as Record<string, unknown>;

  const [changeType, setChangeType] = useState(initialType);
  const [effective, setEffective] = useState(editing?.effective_date ?? "");
  const [name, setName] = useState(editing?.subject_name ?? "");
  const [upn, setUpn] = useState(editing?.subject_upn ?? "");
  const [hrbp, setHrbp] = useState(editing?.hrbp_upn ?? "");
  const [current, setCurrent] = useState<OrgState>(orgFrom(d.current as OrgData | undefined));
  const [next, setNext] = useState<OrgState>(orgFrom(d.new as OrgData | undefined));
  const [note, setNote] = useState((d.note as string | null) ?? "");
  // New hire
  const [fhId, setFhId] = useState(editing?.links_future_hire_id ?? "");
  const [actual, setActual] = useState<MoneyState>(
    moneyFrom(editing?.compensation as Money | null | undefined),
  );
  const it = (d.it_setup ?? {}) as Record<string, unknown>;
  const yesNo = (v: unknown) => (v === undefined || v === null ? "" : v ? "yes" : "no");
  const [itLocation, setItLocation] = useState((it.primary_location as string) ?? "");
  const [computer, setComputer] = useState((it.computer_type as string) ?? "");
  const [mobile, setMobile] = useState(yesNo(it.mobile_phone));
  const [sysAccess, setSysAccess] = useState(yesNo(it.system_access));
  const [referenceUser, setReferenceUser] = useState((it.reference_user_upn as string) ?? "");
  // Exit
  const [onList, setOnList] = useState<string>(
    ex.on_exit_list === undefined ? "" : ex.on_exit_list ? "yes" : "no",
  );
  const [exitDate, setExitDate] = useState((ex.actual_exit_date as string) ?? "");
  const [exitTime, setExitTime] = useState((ex.exit_time as string) ?? "17:00");
  const [zone, setZone] = useState((ex.local_tz as string) ?? BROWSER_TZ);
  const [outcome, setOutcome] = useState((ex.position_outcome as string) ?? "");
  const [replaces, setReplaces] = useState<string>(
    ex.replaces_listed_employee === undefined || ex.replaces_listed_employee === null
      ? ""
      : ex.replaces_listed_employee
        ? "yes"
        : "no",
  );
  const [replacedName, setReplacedName] = useState((ex.replaced_employee_name as string) ?? "");
  const [replacedArea, setReplacedArea] = useState((ex.replaced_employee_area as string) ?? "");
  const [mailboxTo, setMailboxTo] = useState((ex.mailbox_to_upn as string) ?? "");
  const [sendAs, setSendAs] = useState(Boolean(ex.grant_send_as));
  const [onedriveTo, setOnedriveTo] = useState((ex.onedrive_to_upn as string) ?? "");

  const isHire = changeType === "new_hire";
  const isExit = changeType === "exit";
  const isOrg = !isHire && !isExit;

  // Which org rows this change type is about. Everything stays editable;
  // the hint tells HR which ones the workbook expects.
  const expected: OrgKey[] = isHire
    ? ["supervisor_upn", "job_title", "department", "area", "country", "city", "legal_entity"]
    : changeType === "supervisor"
      ? ["supervisor_upn"]
      : changeType === "job"
        ? ["job_title", "department", "area"]
        : changeType === "location"
          ? ["country", "city"]
          : changeType === "legal_entity"
            ? ["legal_entity"]
            : isExit
              ? []
              : ORG_KEYS;

  // The New rows a hire cannot go without: Ivanti's new-user form requires a
  // job title and "Reporting to".
  const requiredOrg: OrgKey[] = isHire ? ["supervisor_upn", "job_title"] : [];
  const orgLabel = (k: OrgKey) => vocab.org_field_labels[k] ?? k;

  const missing = [
    ...stillNeeded([
      [effective, "Effective date"],
      [name.trim().length >= 2, "Name"],
      [isHire || upn.trim(), "Their account"],
      ...requiredOrg.map((k): [unknown, string] => [next[k].trim(), `New ${orgLabel(k)}`]),
      [!isOrg || ORG_KEYS.some((k) => next[k].trim()), "At least one New value"],
    ]),
    ...(isHire
      ? [
          ...stillNeeded([
            [itLocation, "Primary location"],
            [computer, "Type of computer"],
            [mobile, "Mobile phone"],
            [sysAccess, "Business system access"],
            [sysAccess !== "yes" || referenceUser.trim(), "Reference user"],
          ]),
          ...moneyMissing(actual, "Actual"),
        ]
      : []),
    ...(isExit
      ? stillNeeded([
          [onList, "On the Exit List?"],
          [exitDate, "Actual exit date"],
          [outcome, "Position outcome"],
          [onList !== "no" || replaces, "Replaces somebody on the Exit List?"],
          [
            onList !== "no" || replaces !== "yes" || replacedName.trim(),
            "Employee originally named on the Exit List",
          ],
        ])
      : []),
  ];

  const body = (): EmployeeChangeIn => ({
    change_type: changeType,
    effective_date: effective,
    subject_name: name.trim(),
    subject_upn: upn.trim() || null,
    hrbp_upn: hrbp.trim() || null,
    current: orgOut(current),
    new: orgOut(next),
    note: note.trim() || null,
    new_hire: isHire
      ? {
          future_hire_id: fhId.trim() || null,
          actual: moneyOut(actual)!,
          it_setup: {
            primary_location: itLocation,
            computer_type: computer,
            mobile_phone: mobile === "yes",
            system_access: sysAccess === "yes",
            reference_user_upn: sysAccess === "yes" ? referenceUser.trim() || null : null,
          },
        }
      : null,
    exit: isExit
      ? {
          on_exit_list: onList === "yes",
          actual_exit_date: exitDate,
          position_outcome: outcome,
          replaces_listed_employee: onList === "yes" ? null : replaces === "yes",
          replaced_employee_name: replacedName.trim() || null,
          replaced_employee_area: replacedArea.trim() || null,
          exit_time: exitTime,
          local_tz: zone,
          mailbox_to_upn: mailboxTo.trim() || null,
          grant_send_as: sendAs,
          onedrive_to_upn: onedriveTo.trim() || null,
        }
      : null,
  });

  const orgInput = (which: "current" | "new", k: OrgKey) => {
    const state = which === "current" ? current : next;
    const set = which === "current" ? setCurrent : setNext;
    const v = state[k];
    const on = (val: string) => set({ ...state, [k]: val });
    if (k === "supervisor_upn") {
      return (
        <PersonPicker
          label=""
          placeholder="Surname, or supervisor@noricangroup.com"
          value={v}
          onChange={on}
          size="sm"
        />
      );
    }
    if (k === "legal_entity") {
      return (
        <select className={control} value={v} onChange={(e) => on(e.target.value)}>
          <option value="">—</option>
          {vocab.legal_entities.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    if (k === "country") {
      return (
        <select className={control} value={v} onChange={(e) => on(e.target.value)}>
          <option value="">—</option>
          {vocab.countries.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    if (k === "area") {
      return (
        <select className={control} value={v} onChange={(e) => on(e.target.value)}>
          <option value="">—</option>
          {vocab.areas.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    if (k === "brand") {
      return (
        <select className={control} value={v} onChange={(e) => on(e.target.value)}>
          <option value="">—</option>
          {vocab.brands.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    if (k === "city") {
      const cities = state.country
        ? (vocab.cities[state.country] ?? [])
        : Object.values(vocab.cities).flat();
      const listId = `wf-cities-${which}-${state.country || "all"}`;
      return (
        <>
          <Input size="sm" list={listId} value={v} onChange={(e) => on(e.target.value)} />
          <datalist id={listId}>
            {cities.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </>
      );
    }
    return <Input size="sm" value={v} onChange={(e) => on(e.target.value)} />;
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (missing.length > 0) {
          return;
        }
        submit.mutate(
          { kind: "employee_change", body: body(), id: editing?.id },
          { onSuccess: (r) => r && onDone(r.id) },
        );
      }}
    >
      <h2 className="text-fg text-sm font-semibold">Employee change</h2>
      <div className="grid gap-4 md:grid-cols-2">
        <Sel
          label="Change type"
          required
          allowEmpty={false}
          value={changeType}
          onChange={(v) => setChangeType(v as typeof changeType)}
          options={Object.entries(vocab.change_types).map(([value, label]) => ({ value, label }))}
        />
        <Field label="Effective date" required>
          <input
            type="date"
            className={control}
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
          />
        </Field>
        <Field
          label="Name"
          required
          hint={isHire ? "Surname, Given — as it will appear in the directory." : undefined}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {isHire ? null : (
          <PersonPicker
            label="Their account"
            required
            hint="Pick the person; the sign-in name is what the change is applied to."
            placeholder="Surname, or firstname.lastname@noricangroup.com"
            value={upn}
            onChange={setUpn}
          />
        )}
        <PersonPicker
          label="Responsible HRBP"
          hint="Leave empty if that is you."
          placeholder="Surname, or hrbp@noricangroup.com"
          value={hrbp}
          onChange={setHrbp}
        />
      </div>

      {isExit ? null : (
        <fieldset className="border-border rounded border p-3">
          <legend className="text-fg px-1 text-sm font-medium">Organisational data</legend>
          <p className={`${subText} mb-2`}>
            {isHire
              ? "Supervisor and job title are required. Complete the other New fields too, except Brand; IT gets them with the request."
              : "Complete Current and New only for the fields that change. Brand is recorded; the rest is written to the directory on approval."}
          </p>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Field</th>
                {isHire ? null : <th className={th}>Current</th>}
                <th className={th}>New</th>
              </tr>
            </thead>
            <tbody>
              {ORG_KEYS.map((k) => (
                <tr key={k} className={expected.includes(k) ? "" : "opacity-70"}>
                  <td className={`${td} whitespace-nowrap`}>
                    {orgLabel(k)}
                    {requiredOrg.includes(k) ? (
                      <span className="text-danger-fg" aria-hidden="true">
                        {" "}
                        *
                      </span>
                    ) : null}
                    {k === "brand" ? <div className={subText}>not currently required</div> : null}
                  </td>
                  {isHire ? null : <td className={td}>{orgInput("current", k)}</td>}
                  <td className={td}>{orgInput("new", k)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
      )}

      {isHire ? (
        <>
          <div className="border-info-border bg-info-bg rounded border p-3 text-sm">
            <strong>What happens on approval:</strong> HROps files an &quot;Access - New user&quot;
            request for this person in Ivanti for Global IT Helpdesk, with you as the requester,
            carrying the start date, title, supervisor, department, area, entity and location, and
            the IT set-up below. The pay figures are never part of it. HROps creates no account
            itself.
          </div>
          <fieldset className="border-border rounded border p-3">
            <legend className="text-fg px-1 text-sm font-medium">IT set-up</legend>
            <div className="grid gap-4 md:grid-cols-2">
              <Sel
                label="Primary location"
                hint="The site IT sets the person up at."
                required
                value={itLocation}
                onChange={setItLocation}
                options={vocab.it_locations}
              />
              <Sel
                label="Type of computer needed"
                required
                value={computer}
                onChange={setComputer}
                options={vocab.computer_types}
              />
              <Sel
                label="Does this person need a mobile phone?"
                required
                value={mobile}
                onChange={setMobile}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
              />
              <Sel
                label="Will this person need access to any business system?"
                hint="For example D365, CRM or PLM."
                required
                value={sysAccess}
                onChange={setSysAccess}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
              />
              {sysAccess === "yes" ? (
                <PersonPicker
                  label="Reference user"
                  required
                  hint="A colleague whose system access this person should get."
                  placeholder="Surname, or colleague@noricangroup.com"
                  value={referenceUser}
                  onChange={setReferenceUser}
                />
              ) : null}
            </div>
          </fieldset>
          <Sel
            label="Future Hire ID"
            hint="The position this person fills, if it was reported as a future hire."
            value={fhId}
            onChange={setFhId}
            options={(futureHires.data ?? []).map((f) => ({
              value: f.future_hire_id,
              label: `${f.future_hire_id} — ${f.role} (${f.area}, ${f.country})${f.filled_by ? ` · already filled by ${f.filled_by}` : ""}`,
            }))}
          />
          <MoneyFields
            title="Actual compensation"
            prefix="Actual"
            value={actual}
            onChange={setActual}
            vocab={vocab}
          />
        </>
      ) : null}

      {isExit ? (
        <>
          <fieldset className="border-border rounded border p-3">
            <legend className="text-fg px-1 text-sm font-medium">
              Planned-exit reconciliation
            </legend>
            <div className="grid gap-4 md:grid-cols-2">
              <Sel
                label="Was this employee on the existing Exit List?"
                required
                value={onList}
                onChange={setOnList}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
              />
              <Field label="Actual exit date" required>
                <input
                  type="date"
                  className={control}
                  value={exitDate}
                  onChange={(e) => setExitDate(e.target.value)}
                />
              </Field>
              <Sel
                label="Position outcome"
                required
                value={outcome}
                onChange={setOutcome}
                options={vocab.position_outcomes}
              />
              {onList === "no" ? (
                <Sel
                  label="Does this exit replace somebody named on the Exit List?"
                  required
                  value={replaces}
                  onChange={setReplaces}
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ]}
                />
              ) : null}
              {replaces === "yes" && onList === "no" ? (
                <>
                  <Field label="Employee originally named on the Exit List" required>
                    <Input value={replacedName} onChange={(e) => setReplacedName(e.target.value)} />
                  </Field>
                  <Sel
                    label="Their area"
                    value={replacedArea}
                    onChange={setReplacedArea}
                    options={vocab.areas}
                  />
                </>
              ) : null}
            </div>
          </fieldset>
          <fieldset className="border-border rounded border p-3">
            <legend className="text-fg px-1 text-sm font-medium">
              What HROps does when this is approved
            </legend>
            <p className={`${subText} mb-3`}>
              Approval raises the offboarding: the account is disabled in Entra and on-prem AD at
              this time, the mailbox and OneDrive are handed over, and the licence is reclaimed two
              weeks later. A date already past means right away.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-fg-muted flex flex-col gap-1 text-xs">
                Access ends at
                <input
                  type="time"
                  className={control}
                  value={exitTime}
                  onChange={(e) => setExitTime(e.target.value)}
                />
              </label>
              <label className="text-fg-muted flex flex-col gap-1 text-xs">
                Time zone
                <select className={control} value={zone} onChange={(e) => setZone(e.target.value)}>
                  {ZONES.map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <PersonPicker
                label="Who takes the mailbox"
                hint="Full Access; it appears in their Outlook by itself."
                placeholder="Surname, or colleague@noricangroup.com"
                value={mailboxTo}
                onChange={setMailboxTo}
              />
              <PersonPicker
                label="Who takes the OneDrive"
                hint="Read access, under Shared with me."
                placeholder="Surname, or colleague@noricangroup.com"
                value={onedriveTo}
                onChange={setOnedriveTo}
              />
            </div>
            <div className="mt-3">
              <Checkbox.Root
                checked={sendAs}
                onCheckedChange={(c: boolean) => setSendAs(c)}
                disabled={!mailboxTo.trim()}
                label="Also grant Send As (the delegate can send mail as the leaver)"
              />
            </div>
          </fieldset>
        </>
      ) : null}

      <Field label="Note" hint="Optional. For the approver.">
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <SubmitRow
        pending={submit.isPending}
        missing={missing}
        error={submit.error}
        label="Submit for approval"
        editing={Boolean(editing)}
      />
    </form>
  );
}

// ─── PLANNED EXIT ────────────────────────────────────────────────────────────

function PlannedExitForm({
  vocab,
  editing,
  onDone,
}: {
  vocab: WorkforceVocab;
  editing: WorkforceRecord | null;
  onDone: (id: string) => void;
}) {
  const submit = useSubmitWorkforce();
  const d = (editing?.details ?? {}) as Record<string, string | null>;
  const [name, setName] = useState(editing?.subject_name ?? "");
  const [upn, setUpn] = useState(editing?.subject_upn ?? "");
  const [hrbp, setHrbp] = useState(editing?.hrbp_upn ?? "");
  const [area, setArea] = useState(d.area ?? "");
  const [title, setTitle] = useState(d.job_title ?? "");
  const [country, setCountry] = useState(d.country ?? "");
  const [entity, setEntity] = useState(d.legal_entity ?? "");
  const [expected, setExpected] = useState(editing?.effective_date ?? "");
  const [money, setMoney] = useState<MoneyState>(
    moneyFrom(editing?.compensation as Money | null | undefined),
  );
  const [note, setNote] = useState(d.note ?? "");
  const missing = [
    ...stillNeeded([
      [name.trim().length >= 2, "Name"],
      [area, "Area"],
      [title.trim(), "Job title / function"],
      [country, "Country"],
      [entity, "Legal entity"],
      [expected, "Expected exit date"],
    ]),
    ...moneyMissing(money, "Annual"),
  ];

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (missing.length > 0) {
          return;
        }
        const body: PlannedExitIn = {
          subject_name: name.trim(),
          subject_upn: upn.trim() || null,
          hrbp_upn: hrbp.trim() || null,
          area,
          job_title: title.trim(),
          country,
          legal_entity: entity,
          expected_exit_date: expected,
          compensation: moneyOut(money)!,
          note: note.trim() || null,
        };
        submit.mutate(
          { kind: "planned_exit", body, id: editing?.id },
          { onSuccess: (r) => r && onDone(r.id) },
        );
      }}
    >
      <h2 className="text-fg text-sm font-semibold">Planned exit</h2>
      <p className={subText}>
        Only for an additional planned exit that is not yet on the existing Exit List. When the
        person actually leaves, report the exit as an employee change.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <PersonPicker
          label="Their account"
          hint="Optional here; it is needed when the exit is reported."
          placeholder="Surname, or firstname.lastname@noricangroup.com"
          value={upn}
          onChange={setUpn}
        />
        <Sel label="Area" required value={area} onChange={setArea} options={vocab.areas} />
        <Field label="Job title / function" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Sel
          label="Country"
          required
          value={country}
          onChange={setCountry}
          options={vocab.countries}
        />
        <Sel
          label="Legal entity"
          required
          value={entity}
          onChange={setEntity}
          options={vocab.legal_entities}
        />
        <Field label="Expected exit date" required>
          <input
            type="date"
            className={control}
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
          />
        </Field>
        <PersonPicker
          label="Responsible HRBP"
          hint="Leave empty if that is you."
          placeholder="Surname, or hrbp@noricangroup.com"
          value={hrbp}
          onChange={setHrbp}
        />
      </div>
      <MoneyFields
        title="Compensation"
        prefix="Annual"
        value={money}
        onChange={setMoney}
        vocab={vocab}
      />
      <Field label="Note" hint="Optional. For the approver.">
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <SubmitRow
        pending={submit.isPending}
        missing={missing}
        error={submit.error}
        label="Submit for approval"
        editing={Boolean(editing)}
      />
    </form>
  );
}

// ─── FUTURE HIRE ─────────────────────────────────────────────────────────────

function FutureHireForm({
  vocab,
  editing,
  onDone,
}: {
  vocab: WorkforceVocab;
  editing: WorkforceRecord | null;
  onDone: (id: string) => void;
}) {
  const submit = useSubmitWorkforce();
  const d = (editing?.details ?? {}) as Record<string, string | null>;
  const [area, setArea] = useState(d.area ?? "");
  const [wave, setWave] = useState(d.wave ?? "");
  const [role, setRole] = useState(d.role ?? "");
  const [supervisor, setSupervisor] = useState(d.supervisor_upn ?? "");
  const [country, setCountry] = useState(d.country ?? "");
  const [city, setCity] = useState(d.city ?? "");
  const [entity, setEntity] = useState(d.legal_entity ?? "");
  const [quarter, setQuarter] = useState(d.expected_hire_quarter ?? "");
  const [hrbp, setHrbp] = useState(editing?.hrbp_upn ?? "");
  const comp = (editing?.compensation ?? null) as { target?: Money } | null;
  const [money, setMoney] = useState<MoneyState>(moneyFrom(comp?.target));
  const [note, setNote] = useState(d.note ?? "");
  const missing = [
    ...stillNeeded([
      [area, "Area"],
      [wave, "Wave"],
      [role.trim().length >= 2, "Description / role"],
      [supervisor.trim(), "Supervisor"],
      [country, "Country"],
      [city.trim(), "City"],
      [entity, "Legal entity"],
      [quarter, "Expected hire quarter"],
    ]),
    ...moneyMissing(money, "Target"),
  ];

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (missing.length > 0) {
          return;
        }
        const body: FutureHireIn = {
          area,
          wave,
          role: role.trim(),
          supervisor_upn: supervisor.trim(),
          country,
          city: city.trim(),
          legal_entity: entity,
          expected_hire_quarter: quarter,
          hrbp_upn: hrbp.trim() || null,
          target: moneyOut(money)!,
          note: note.trim() || null,
        };
        submit.mutate(
          { kind: "future_hire", body, id: editing?.id },
          { onSuccess: (r) => r && onDone(r.id) },
        );
      }}
    >
      <h2 className="text-fg text-sm font-semibold">Future hire</h2>
      <p className={subText}>
        An anonymous future position. The <HelpTerm id="future-hire-id">Future Hire ID</HelpTerm> is
        assigned when you submit
        {editing?.future_hire_id ? ` (this one is ${editing.future_hire_id})` : ""}. Record target
        compensation now; the actual figures come with the new hire that fills it.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Sel label="Area" required value={area} onChange={setArea} options={vocab.areas} />
        <Sel label="Wave" required value={wave} onChange={setWave} options={vocab.waves} />
        <Field label="Description / role" required>
          <Input value={role} onChange={(e) => setRole(e.target.value)} />
        </Field>
        <PersonPicker
          label="Supervisor"
          required
          placeholder="Surname, or supervisor@noricangroup.com"
          value={supervisor}
          onChange={setSupervisor}
        />
        <Sel
          label="Country"
          required
          value={country}
          onChange={setCountry}
          options={vocab.countries}
        />
        <CityInput vocab={vocab} country={country} value={city} onChange={setCity} required />
        <Sel
          label="Legal entity"
          required
          value={entity}
          onChange={setEntity}
          options={vocab.legal_entities}
        />
        <Sel
          label="Expected hire quarter"
          required
          value={quarter}
          onChange={setQuarter}
          options={vocab.hire_quarters}
        />
        <PersonPicker
          label="Responsible HRBP"
          hint="Leave empty if that is you."
          placeholder="Surname, or hrbp@noricangroup.com"
          value={hrbp}
          onChange={setHrbp}
        />
      </div>
      <MoneyFields
        title="Target compensation"
        prefix="Target"
        value={money}
        onChange={setMoney}
        vocab={vocab}
      />
      <Field label="Note" hint="Optional. For the approver.">
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <SubmitRow
        pending={submit.isPending}
        missing={missing}
        error={submit.error}
        label="Submit for approval"
        editing={Boolean(editing)}
      />
    </form>
  );
}

// ─── Requests ────────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<
  WorkforceRecord["status"],
  { label: string; tone: BadgeProps["tone"]; variant: BadgeProps["variant"] }
> = {
  submitted: { label: "awaiting approval", tone: "info", variant: "subtle" },
  approved: { label: "approved", tone: "success", variant: "subtle" },
  returned: { label: "sent back", tone: "warning", variant: "solid" },
  withdrawn: { label: "withdrawn", tone: "neutral", variant: "outline" },
};

function WfStatus({ status }: { status: WorkforceRecord["status"] }) {
  const s = STATUS_STYLE[status];
  return (
    <Badge size="xs" tone={s.tone} variant={s.variant}>
      {s.label}
    </Badge>
  );
}

function kindLabel(r: WorkforceRecord, vocab: WorkforceVocab): string {
  if (r.kind === "employee_change") {
    return vocab.change_types[r.change_type ?? ""] ?? "Employee change";
  }
  return r.kind === "planned_exit" ? "Planned exit" : `Future hire ${r.future_hire_id ?? ""}`;
}

function RequestsView({
  vocab,
  items,
  approver,
  selected,
  onSelect,
  onEdit,
  loading,
  error,
}: {
  vocab: WorkforceVocab;
  items: WorkforceRecord[];
  approver: boolean;
  selected: string | null;
  onSelect: (id: string | null) => void;
  onEdit: (r: WorkforceRecord) => void;
  loading: boolean;
  error: unknown;
}) {
  const [status, setStatus] = useState<"" | WorkforceRecord["status"]>("");
  const rows = useMemo(
    () => (status ? items.filter((i) => i.status === status) : items),
    [items, status],
  );
  const waiting = rows.filter((r) => r.status === "submitted");
  const rest = rows.filter((r) => r.status !== "submitted");

  if (loading) {
    return <Notice>Loading…</Notice>;
  }
  if (error) {
    return <Notice tone="danger">{errorMessage(error)}</Notice>;
  }
  return (
    <section className="mt-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-fg text-sm font-semibold">
          {approver ? "Requests, yours to approve first" : "Requests"}
        </h2>
        <select
          className={control}
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_STYLE).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>
      {rows.length === 0 ? (
        <Notice>Nothing here yet.</Notice>
      ) : (
        <>
          {waiting.length ? (
            <RequestTable rows={waiting} vocab={vocab} selected={selected} onSelect={onSelect} />
          ) : null}
          {rest.length ? (
            <>
              {waiting.length ? (
                <h3 className="text-fg-muted mt-6 text-xs font-medium">Decided</h3>
              ) : null}
              <RequestTable rows={rest} vocab={vocab} selected={selected} onSelect={onSelect} />
            </>
          ) : null}
        </>
      )}
      {selected ? (
        <RecordView id={selected} vocab={vocab} approver={approver} onEdit={onEdit} />
      ) : null}
    </section>
  );
}

function RequestTable({
  rows,
  vocab,
  selected,
  onSelect,
}: {
  rows: WorkforceRecord[];
  vocab: WorkforceVocab;
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <table className={`${table} mt-2`}>
      <thead>
        <tr>
          <th className={th}>Who</th>
          <th className={th}>What</th>
          <th className={th}>Effective</th>
          <th className={th}>Submitted</th>
          <th className={th}>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.id}
            className={`hover:bg-bg-subtle cursor-pointer ${selected === r.id ? "bg-bg-subtle" : ""}`}
            onClick={() => onSelect(selected === r.id ? null : r.id)}
          >
            <td className={`${td} font-medium`}>
              {r.subject_name}
              {r.subject_upn ? <div className={subText}>{r.subject_upn}</div> : null}
            </td>
            <td className={td}>{kindLabel(r, vocab)}</td>
            <td className={td}>{r.effective_date ?? "—"}</td>
            <td className={td}>
              {dateInZone(r.submitted_at)}
              <div className={subText}>{r.submitted_by_upn}</div>
            </td>
            <td className={td}>
              <WfStatus status={r.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── one record ──────────────────────────────────────────────────────────────

function RecordView({
  id,
  vocab,
  approver,
  onEdit,
}: {
  id: string;
  vocab: WorkforceVocab;
  approver: boolean;
  onEdit: (r: WorkforceRecord) => void;
}) {
  const me = useMe().data;
  const { data, isLoading, error } = useWorkforceChange(id);
  const decide = useWorkforceDecision();
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState<"approve" | "return" | "withdraw" | null>(null);

  if (isLoading) {
    return <Notice>Loading…</Notice>;
  }
  if (error || !data) {
    return <Notice tone="danger">{errorMessage(error)}</Notice>;
  }
  const r = data.record;
  const d = (r.details ?? {}) as Record<string, unknown>;
  const mine = (r.submitted_by_upn ?? "").toLowerCase() === (me?.email ?? "").toLowerCase();
  const editable = (r.status === "submitted" || r.status === "returned") && (mine || approver);
  const decidable = approver && r.status === "submitted";
  const act = (action: "approve" | "return" | "withdraw") =>
    decide.mutate(
      { id, action, note: note.trim() || undefined },
      {
        onSuccess: () => {
          setConfirming(null);
          setNote("");
        },
      },
    );

  return (
    <div className="border-border mt-6 rounded border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-fg text-sm font-semibold">
          {kindLabel(r, vocab)}: {r.subject_name}
          {r.subject_upn ? <span className={`${subText} ml-2`}>{r.subject_upn}</span> : null}
        </h3>
        <WfStatus status={r.status} />
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
        <Row k="Effective" v={r.effective_date ?? "—"} />
        <Row k="Responsible HRBP" v={r.hrbp_upn} />
        <Row k="Submitted" v={`${dateInZone(r.submitted_at)} by ${r.submitted_by_upn}`} />
        {r.approved_at ? (
          <Row k="Approved" v={`${dateInZone(r.approved_at)} by ${r.approver_upn ?? ""}`} />
        ) : null}
        {r.returned_at ? (
          <Row k="Sent back" v={`${dateInZone(r.returned_at)} by ${r.approver_upn ?? ""}`} />
        ) : null}
        {r.decision_note ? <Row k="Approver's note" v={r.decision_note} /> : null}
        {r.links_future_hire_id ? <Row k="Fills Future Hire" v={r.links_future_hire_id} /> : null}
        {typeof d.note === "string" && d.note ? <Row k="Note" v={d.note} /> : null}
        {r.service_request_status ? (
          <Row
            k="Ivanti request"
            v={
              r.service_request_status === "raised"
                ? `SR ${r.service_request_number}, Access - New user`
                : r.service_request_status === "queued"
                  ? `queued, filed within a few minutes${r.service_request_error ? ` (last try failed: ${r.service_request_error})` : ""}`
                  : `Ivanti refused it${r.service_request_error ? `: ${r.service_request_error}` : ""}. SecOps sees it in the operations view; nothing was mailed.`
            }
          />
        ) : null}
      </dl>

      <Details r={r} vocab={vocab} />

      <Compensation r={r} />

      {r.outcome ? (
        <div className="mt-4">
          <h4 className="text-fg-muted text-xs font-medium">What approval did</h4>
          <ul className="mt-1 list-disc pl-5 text-sm">
            {Object.entries(r.outcome as Record<string, string>).map(([k, v]) => (
              <li key={k}>
                <span className="text-fg-muted">{OUTCOME_LABEL[k] ?? k.replace(/_/g, " ")}:</span>{" "}
                {v}
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap gap-4 text-sm">
            {data.offboarding ? (
              <Link className="underline" to={`/leaver/${data.offboarding.id}`}>
                Offboarding record <StatusBadge status={data.offboarding.status} />
                <span className={`${subText} ml-1`}>
                  effective{" "}
                  {inZoneSentence(data.offboarding.effective_at, data.offboarding.local_tz)}
                </span>
              </Link>
            ) : null}
            {data.scenario ? (
              <span>
                AD write: <strong>{data.scenario.status}</strong>
                <span className={`${subText} ml-1`}>
                  {data.scenario.verified_count}/{data.scenario.change_count} verified in Entra
                </span>
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {decidable || editable ? (
        <div className="border-border mt-4 border-t pt-3">
          {confirming ? (
            <div
              className={`rounded border p-3 ${confirming === "approve" ? "border-warning-border bg-warning-bg" : "border-border"}`}
            >
              <p className="text-fg text-sm">
                {confirming === "approve"
                  ? approveSentence(r)
                  : confirming === "return"
                    ? "Send it back to the submitter. Say what needs changing; they edit and resubmit."
                    : "Withdraw this request. It stays visible, marked withdrawn, and nothing happens."}
              </p>
              <Textarea
                className="mt-2"
                rows={2}
                placeholder={
                  confirming === "return" ? "What needs changing (required)" : "Note (optional)"
                }
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="mt-2 flex items-center gap-3">
                <Button
                  size="sm"
                  disabled={decide.isPending || (confirming === "return" && note.trim().length < 3)}
                  onClick={() => act(confirming)}
                >
                  {decide.isPending
                    ? "Working…"
                    : confirming === "approve"
                      ? "Approve and do it"
                      : confirming === "return"
                        ? "Send back"
                        : "Withdraw"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                  Not now
                </Button>
              </div>
              {decide.error ? (
                <p className="text-danger-fg mt-2 text-sm">{errorMessage(decide.error)}</p>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              {decidable ? (
                <>
                  <Button size="sm" onClick={() => setConfirming("approve")}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirming("return")}>
                    Send back
                  </Button>
                </>
              ) : null}
              {editable ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => onEdit(r)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming("withdraw")}>
                    Withdraw
                  </Button>
                </>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      <details className="mt-4">
        <summary className={`${subText} cursor-pointer`}>
          Log ({(data.actions ?? []).length})
        </summary>
        <ActionLog entries={data.actions ?? []} tz={null} />
      </details>
    </div>
  );
}

const OUTCOME_LABEL: Record<string, string> = {
  offboarding: "Offboarding",
  active_directory: "Active Directory",
  helpdesk: "Global IT Helpdesk",
  future_hire: "Future hire",
  brand: "Brand",
  record: "Record",
};

function approveSentence(r: WorkforceRecord): string {
  if (r.kind === "employee_change" && r.change_type === "exit") {
    return "Approving raises the offboarding: the account is disabled in Entra and on-prem AD at the exit time, the mailbox and OneDrive are handed over, and the licence is reclaimed two weeks later. HR can still revert within those two weeks.";
  }
  if (r.kind === "employee_change" && r.change_type === "new_hire") {
    return "Approving records the hire and raises a New User Request for Global IT Helpdesk in Ivanti, without the compensation figures, with the person who reported it as the requester. HROps creates no account itself.";
  }
  if (r.kind === "employee_change") {
    return "Approving writes the new values to the person's account in Active Directory within a few minutes, under your name. Brand is recorded only.";
  }
  return "Approving records it. Nothing is changed in any system.";
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-fg-muted w-36 shrink-0">{k}</dt>
      <dd className="text-fg">{v}</dd>
    </div>
  );
}

function Details({ r, vocab }: { r: WorkforceRecord; vocab: WorkforceVocab }) {
  const d = (r.details ?? {}) as Record<string, unknown>;
  if (r.kind === "employee_change") {
    const cur = (d.current ?? {}) as Record<string, string>;
    const nxt = (d.new ?? {}) as Record<string, string>;
    const ex = (d.exit ?? null) as Record<string, unknown> | null;
    const it = (d.it_setup ?? null) as Record<string, unknown> | null;
    const keys = ORG_KEYS.filter((k) => cur[k] || nxt[k]);
    return (
      <>
        {keys.length ? (
          <table className={`${table} mt-3`}>
            <thead>
              <tr>
                <th className={th}>Field</th>
                <th className={th}>Current</th>
                <th className={th}>New</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k}>
                  <td className={td}>{vocab.org_field_labels[k] ?? k}</td>
                  <td className={`${td} text-fg-muted`}>{cur[k] ?? "—"}</td>
                  <td className={`${td} font-medium`}>{nxt[k] ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {ex ? (
          <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
            <Row k="On the Exit List" v={ex.on_exit_list ? "yes" : "no"} />
            <Row k="Actual exit date" v={String(ex.actual_exit_date ?? "—")} />
            <Row k="Position outcome" v={String(ex.position_outcome ?? "—")} />
            {ex.replaces_listed_employee !== null && ex.replaces_listed_employee !== undefined ? (
              <Row
                k="Replaces a listed employee"
                v={
                  ex.replaces_listed_employee
                    ? `yes: ${ex.replaced_employee_name ?? ""}${ex.replaced_employee_area ? ` (${ex.replaced_employee_area})` : ""}`
                    : "no"
                }
              />
            ) : null}
            <Row k="Access ends" v={`${ex.actual_exit_date} ${ex.exit_time} ${ex.local_tz}`} />
            <Row k="Mailbox to" v={String(ex.mailbox_to_upn ?? "nobody")} />
            <Row k="OneDrive to" v={String(ex.onedrive_to_upn ?? "nobody")} />
          </dl>
        ) : null}
        {it ? (
          <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
            <Row k="IT location" v={String(it.primary_location ?? "—")} />
            <Row k="Computer" v={String(it.computer_type ?? "—")} />
            <Row k="Mobile phone" v={it.mobile_phone ? "yes" : "no"} />
            <Row
              k="System access"
              v={it.system_access ? `yes, same as ${String(it.reference_user_upn ?? "—")}` : "no"}
            />
          </dl>
        ) : null}
      </>
    );
  }
  const entries = Object.entries(d).filter(([k, v]) => k !== "note" && v);
  return (
    <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
      {entries.map(([k, v]) => (
        <Row key={k} k={k.replace(/_/g, " ")} v={String(v)} />
      ))}
    </dl>
  );
}

function Compensation({ r }: { r: WorkforceRecord }) {
  if (!r.compensation_visible) {
    return (
      <p className={`${subText} mt-3`}>
        Compensation is on this record and is not shown to you: only the submitter, the responsible
        HRBP and the approvers see it.
      </p>
    );
  }
  const c = r.compensation as (Money & { target?: Money }) | null;
  if (!c) {
    return null;
  }
  const m = c.target ?? c;
  const label = c.target ? "Target compensation" : "Actual compensation";
  const fmt = (n: number) => new Intl.NumberFormat("en-GB").format(n);
  return (
    <div className="mt-3">
      <h4 className="text-fg-muted text-xs font-medium">{label}</h4>
      <p className="text-fg text-sm">
        Salary {fmt(m.salary)} · Benefits {fmt(m.benefits)} · Employer cost {fmt(m.employer_cost)}{" "}
        {m.currency}
      </p>
      <p className={subText}>
        Shown to you because you submitted it, are the HRBP, or approve. Logged.
      </p>
    </div>
  );
}

// ─── the log ─────────────────────────────────────────────────────────────────

const ACTION_LABEL: Record<string, string> = {
  "workforce.submit": "submitted",
  "workforce.update": "edited and resubmitted",
  "workforce.approve": "approved",
  "workforce.return": "sent back",
  "workforce.withdraw": "withdrawn",
  "workforce.read": "compensation viewed",
  "workforce.export": "exported to Excel",
  "offboarding.created": "offboarding raised",
  "org.adhoc.create": "AD change prepared",
  "org.adhoc.confirm": "AD change queued",
  "org.adhoc.write": "AD change sent to the worker",
  mail_send: "mail sent",
};

/**
 * Every row the Workforce page caused, newest first: its own actions, the
 * offboarding and AD rows an approval created, and the mails the worker sent.
 * Same append-only table as everywhere else; this is a view of it, not a
 * second log.
 */
function LogView({ enabled }: { enabled: boolean }) {
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const log = useWorkforceLog(enabled, { action, q });
  const navigate = useNavigate();
  if (log.isLoading) {
    return <Notice>Loading the log…</Notice>;
  }
  if (log.error || !log.data) {
    return <Notice tone="danger">{errorMessage(log.error)}</Notice>;
  }
  const items = log.data.items ?? [];
  const actions = log.data.actions ?? [];
  return (
    <section className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-fg text-sm font-semibold">
          Log
          <span className="text-fg-muted ml-2 font-normal">
            {log.data.total} {log.data.total === 1 ? "entry" : "entries"}
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <select className={control} value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {ACTION_LABEL[a] ?? a}
              </option>
            ))}
          </select>
          <Input
            size="sm"
            placeholder="Person, actor or text…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>
      <p className={`${subText} mt-1`}>
        Append-only. A correction is a new row, never an edit. Click a row to open its record.
      </p>
      {items.length === 0 ? (
        <Notice>Nothing logged yet.</Notice>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>When</th>
                <th className={th}>Who</th>
                <th className={th}>What</th>
                <th className={th}>About</th>
                <th className={th}>Outcome</th>
                <th className={th}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const req = (e.request ?? {}) as Record<string, unknown>;
                const wid = typeof req.workforce_id === "string" ? req.workforce_id : null;
                const ref = typeof req.ref_id === "string" ? req.ref_id : null;
                const target = wid ?? ref;
                const t = inZone(e.ts, null);
                return (
                  <tr
                    key={e.id}
                    className={target ? "hover:bg-bg-subtle cursor-pointer" : ""}
                    onClick={() => target && navigate(`/workforce/${target}`)}
                  >
                    <td className={`${td} whitespace-nowrap`}>
                      {t.time}
                      <div className={subText}>{t.zone}</div>
                    </td>
                    <td className={td}>
                      {e.actor_upn ?? "—"}
                      <div className={subText}>{e.actor_app}</div>
                    </td>
                    <td className={td}>{ACTION_LABEL[e.action] ?? e.action}</td>
                    <td className={td}>
                      {e.subject_upn ?? (typeof req.to === "string" ? `to ${req.to}` : "—")}
                    </td>
                    <td className={td}>
                      <OutcomeBadge outcome={e.outcome} />
                    </td>
                    <td className={`${td} max-w-md`}>
                      <LogDetail entry={e} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** One line a person can read, picked from what the row carries. */
function LogDetail({
  entry,
}: {
  entry: { request?: unknown; response?: unknown; error?: string | null };
}) {
  const req = (entry.request ?? {}) as Record<string, unknown>;
  const res = (entry.response ?? {}) as Record<string, unknown>;
  if (entry.error) {
    return <span className="text-danger-fg">{entry.error}</span>;
  }
  const bits: string[] = [];
  if (typeof req.subject === "string") {
    bits.push(req.subject);
  }
  if (typeof req.note === "string" && req.note) {
    bits.push(`note: ${req.note}`);
  }
  if (typeof req.kind === "string") {
    bits.push(req.kind.replace(/_/g, " "));
  }
  if (typeof req.change_type === "string") {
    bits.push(req.change_type.replace(/_/g, " "));
  }
  if (typeof req.subject_name === "string") {
    bits.push(req.subject_name);
  }
  if (req.compensation === true) {
    bits.push("pay figures shown");
  }
  for (const k of ["offboarding", "active_directory", "helpdesk", "future_hire"]) {
    if (typeof res[k] === "string") {
      bits.push(`${OUTCOME_LABEL[k] ?? k}: ${res[k]}`);
    }
  }
  if (typeof req.field === "string") {
    bits.push(`${req.field}: ${String(req.old ?? "—")} → ${String(req.new ?? "")}`);
  }
  if (typeof res.state === "string") {
    bits.push(`AD: ${res.state}`);
  }
  if (typeof req.subject === "string" && typeof req.to === "string") {
    bits.length = 0;
    bits.push(req.subject);
  }
  return <span className={subText}>{bits.join(" · ") || "—"}</span>;
}
